import {
  getBlock,
  makeId,
  type ArticleBody,
  type ContentBlock,
} from "@src/packages/content";
import type { TranslationFunction } from "@src/packages/i18n";
import type { Article as LearnArticle } from "../../components/ArticleSectionCard";

// Curated per-outcome reading list. The `post-cycle` tag alone mixes wait-week
// guidance, retrieval recovery, and preservation content — none of which make
// sense to hand a patient who just got a positive beta, or to hand a patient
// who just closed a negative cycle. Keep the list short and outcome-specific.
const POST_CYCLE_ARTICLE_IDS: Record<"positive" | "negative", string[]> = {
  positive: ["new-article-transition-art-to-pregnancy"],
  negative: [
    "new-article-dealing-with-art-failure",
    "new-article-emotional-support",
  ],
};

function toLearnArticle(
  block: ContentBlock<"article">,
  postCycleLabel: string,
  t: TranslationFunction,
): LearnArticle {
  const body = block.body as ArticleBody;
  return {
    id: block.id,
    title: block.title,
    time: block.readTimeMinutes
      ? t("learn.read_time_minutes", {
          params: { minutes: String(block.readTimeMinutes) },
        })
      : "",
    tag: postCycleLabel,
    teaser: block.subtitle ?? "",
    body,
    tags: block.tags,
  };
}

export function getPostCycleArticles(
  outcome: "positive" | "negative",
  postCycleLabel: string,
  t: TranslationFunction,
): LearnArticle[] {
  return POST_CYCLE_ARTICLE_IDS[outcome]
    .map((id) => getBlock(makeId(id)))
    .filter(
      (block): block is ContentBlock<"article"> => block?.kind === "article",
    )
    .map((block) => toLearnArticle(block, postCycleLabel, t));
}
